// Production-safe logging utility
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  cspAccountId?: string;
  accountCount?: number;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  // Extended properties for AI Assistant logging
  error?: string;
  stack?: string;
  componentStack?: string;
  duration?: number;
  messageCount?: number;
  requestId?: string;
  topic?: string;
  ruleId?: string;
  tokensUsed?: number;
  messageId?: string;
  totalCleanupFunctions?: number;
  operation?: string;
  operationId?: string;
  cleanupFunctions?: number;
  activeOperations?: number;
  timeout?: number;
  threshold?: number;
  conversationLength?: number;
  processingDuration?: number;
  totalDuration?: number;
  success?: boolean;
  context?: any;
  cached?: boolean;
  metadata?: Record<string, any>;
  limitInfo?: any; // For usage limit information
  // Additional properties for enhanced streaming
  language?: string;
  streaming?: boolean;
  fallbackUsed?: boolean;
  usage?: any;
  attempt?: number;
  inactiveMs?: number;
  eventType?: string;
  connectionCount?: number;
  // Missing properties that were causing errors
  originalError?: string;
  chunkLength?: number;
  // New properties to fix TypeScript errors
  fullContentLength?: number;
  contentLength?: number;
  isLoading?: boolean;
  emergencyMode?: boolean;
  isTyping?: boolean;
  suggestionsCount?: number;
  stream?: boolean;
  userMessage?: string;
  // Additional missing properties
  mode?: string;
  model?: string;
  activeCount?: number;
  data?: any;
  processingTime?: number; // Added missing processingTime property
  // New properties to fix current TypeScript errors
  activeRequestId?: string;
  totalCount?: number;
  regularCount?: number;
  streamingCount?: number;
  // Fix current build errors
  mapSize?: number;
  hasRef?: boolean;
  url?: string;
  mapKeys?: string[];
  initialized?: boolean;
  bodyKeys?: string[];
  // Fix latest TypeScript errors
  hasFallback?: boolean;
  pendingCount?: number;
  sequence?: number;
  eventSequence?: number;
  hasStartEventFired?: boolean;
  type?: string;
  // Fix maxRetries TypeScript error
  maxRetries?: number;
  // Fix streaming state management errors
  is_streaming?: boolean;
  is_streaming_value?: boolean;
  updateFields?: string[];
  originalValue?: any;
  count?: number;
  final_is_streaming?: boolean;
  isStreaming?: boolean;
  completed?: boolean;
  final_isStreaming?: boolean;
}

class ProductionLogger {
  // Only allow critical errors and warnings in production
  error(message: string, context?: LogContext, error?: Error): void {
    console.error(`❌ ${message}`, {
      ...context,
      error: error?.message,
      stack: isDevelopment ? error?.stack : undefined,
      timestamp: new Date().toISOString()
    });
  }

  warn(message: string, context?: LogContext): void {
    console.warn(`⚠️ ${message}`, {
      ...context,
      timestamp: new Date().toISOString()
    });
  }

  // Development-only logging
  info(message: string, context?: LogContext): void {
    if (isDevelopment) {
      console.info(`ℹ️ ${message}`, context);
    }
  }

  debug(message: string, context?: LogContext): void {
    if (isDevelopment) {
      console.debug(`🐛 ${message}`, context);
    }
  }

  // OAuth-specific logging with security considerations
  oauthEvent(event: string, success: boolean, context?: LogContext): void {
    if (isDevelopment) {
      console.log(`🔐 OAuth ${event}: ${success ? 'SUCCESS' : 'FAILED'}`, context);
    } else if (!success) {
      // Only log failures in production
      this.warn(`OAuth ${event} failed`, context);
    }
  }

  // Performance logging
  performance(operation: string, duration: number, context?: LogContext): void {
    if (isDevelopment) {
      console.log(`⚡ Performance: ${operation} took ${duration}ms`, context);
    }
  }

  // Security-sensitive logging (never logs sensitive data)
  security(event: string, severity: 'low' | 'medium' | 'high' | 'critical', context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);
    
    if (severity === 'critical' || severity === 'high') {
      this.error(`Security Event: ${event}`, { ...sanitizedContext, severity });
    } else {
      this.warn(`Security Event: ${event}`, { ...sanitizedContext, severity });
    }
  }

  // Remove sensitive data from log context
  private sanitizeContext(context?: LogContext): LogContext | undefined {
    if (!context) return undefined;

    const sanitized = { ...context };
    
    // Remove sensitive fields
    if (sanitized.metadata) {
      const { accessToken, refreshToken, password, secret, ...safeMeta } = sanitized.metadata;
      sanitized.metadata = safeMeta;
    }

    return sanitized;
  }

  // Conditional logging based on environment
  conditionalLog(level: 'info' | 'debug' | 'warn' | 'error', message: string, context?: LogContext): void {
    switch (level) {
      case 'info':
        this.info(message, context);
        break;
      case 'debug':
        this.debug(message, context);
        break;
      case 'warn':
        this.warn(message, context);
        break;
      case 'error':
        this.error(message, context);
        break;
    }
  }
}

// Export singleton instance
export const logger = new ProductionLogger();

// Utility functions for common logging patterns
export const logOAuthFlow = (step: string, success: boolean, context?: LogContext) => {
  logger.oauthEvent(step, success, context);
};

export const logPerformance = (operation: string, startTime: number, context?: LogContext) => {
  const duration = Date.now() - startTime;
  logger.performance(operation, duration, context);
};

export const logSecurityEvent = (event: string, severity: 'low' | 'medium' | 'high' | 'critical', context?: LogContext) => {
  logger.security(event, severity, context);
};

export const logError = (message: string, error?: Error, context?: LogContext) => {
  logger.error(message, context, error);
};

export const logWarning = (message: string, context?: LogContext) => {
  logger.warn(message, context);
};

// Development-only logging
export const logDebug = (message: string, context?: LogContext) => {
  logger.debug(message, context);
};

export const logInfo = (message: string, context?: LogContext) => {
  logger.info(message, context);
};
